/* eslint-disable max-lines */
import {isProperty} from '@optimaxdev/utils';
import {createSelector} from '@reduxjs/toolkit';

import {PRODUCT_TYPES} from 'constants/product';

import {CartItemAccessoryType, CartItemType, UpsellOfferType} from 'desktop/pages/cart/types';
import {ApplicationStoreType} from 'desktop/store';

import {ColorType} from 'types/types';

import {BADGES} from '../../constants/badges';
import {AccessoryType, AccessoryWindowType} from '../reducers/accessories/accessoriesType';

/**
 * Get cart state
 * @param {ApplicationStoreType} state - store state
 * @returns {ApplicationStoreType['cart']} cart state
 */
export const getCart = (state: ApplicationStoreType): ApplicationStoreType['cart'] => state.cart;

/**
 * Selector to get quote items
 */
export const getQuoteItems = (state: ApplicationStoreType) => state.cart.quoteItems;

/**
 * Get cart state
 * @param {ApplicationStoreType} state - store state
 * @returns {string} total price
 */
// @ts-expect-error
export const getCartTotalPrice = createSelector(getCart, (cart): number => cart.totals.total);

/**
 * Does cart contain any contact lenses?
 * @param {ApplicationStoreType} store - Application store
 * @returns {boolean} flag "hasCartContactLenses"
 */
export const hasCartContactLenses = createSelector(
    getQuoteItems,
    (quoteItems: Array<CartItemType>): boolean =>
        Boolean(
            quoteItems.some(({active, type}) => type === PRODUCT_TYPES.CONTACT_LENSES && active),
        ),
);

/**
 * Is checkout login forced
 * @param {ApplicationStoreType} store - Application store
 * @return {Array<CartItemType>} cart full of lenses
 */
export const isFullLensesCart = createSelector(
    getQuoteItems,
    (items: Array<CartItemType>) =>
        Boolean(items.length) && items.every(({type}) => type === PRODUCT_TYPES.CONTACT_LENSES),
);

/**
 * Determines if cart is loading
 *
 * @param {ApplicationStoreType} store - application store
 * @return {boolean} true if cart is loading
 */
export const isCartLoading = (store: ApplicationStoreType): boolean =>
    (store.cart.status === 'pending' && !store.cart.count) ||
    store.cart.specialOffer.status !== 'fulfilled';

/**
 * Get items count
 * @param {ApplicationStoreType} store - Application store
 * @return {number} items count
 */
export const getItemsCount = (store: ApplicationStoreType): number => store.cart.count;

/**
 * Get all the items by field active and count them, contact lenses is considered to be one item
 * @param {ApplicationStoreType} store - Application store
 * @return {Array<CartItemType>} return active items
 */
export const getCartItemsCount = createSelector(getQuoteItems, (items: Array<CartItemType>) =>
    items.reduce(
        (sum, {qty, type}) => sum + (type === PRODUCT_TYPES.CONTACT_LENSES ? 1 : Number(qty)),
        0,
    ),
);

/**
 * Memoized selector which return true if cart has out of stock eyeglasses
 * @param {ApplicationStoreType} - application store
 * @return {boolean} flag that is true if there is out of stock items in cart
 */
export const getHasOutOfStock = createSelector(
    [getQuoteItems],
    (items: Array<CartItemType>): boolean =>
        items.some(({stock}: CartItemType) => stock && stock.isOutOfStock),
);

/**
 * Get if there is at least one of the quote items has a subscription
 * @param {ApplicationStoreType} store - application store
 * @returns {boolean} - whether at least one of the quote items has a subscription
 */
export const getIsCartHasActiveSubscriptionCL = createSelector(
    getQuoteItems,
    (items: Array<CartItemType>) =>
        items
            .filter(item => item.active === true)
            // @ts-expect-error
            .some(item => Boolean(item?.subscription?.subscriptionMode) === true),
);

/**
 * Get is cart loaded
 * @param {ApplicationStoreType} - application store
 * @returns {boolean} - is loaded
 */
export const getIsCartLoaded = isProperty('cart.status', 'fulfilled');

/**
 * Return accessories list on cart
 * @param {ApplicationStoreType} state - app store
 * @returns {Array<AccessoryWindowType>} accessories list
 */
export const getCartAccessories = (state: ApplicationStoreType): Array<AccessoryWindowType> =>
    state.cart.accessories.windowProducts;

/**
 * Return simple in stock accessories list on cart
 * @param {ApplicationStoreType} state - app store
 * @returns {Array<AccessoryType>} simple accessories list
 */
const getCartAccessoriesSimple = (state: ApplicationStoreType): Array<AccessoryType> =>
    state.cart.accessories.simpleProducts.filter(
        ({is_in_stock: isInStock}: AccessoryType): boolean => isInStock,
    );

/**
 * Simple accessories with certain parent id
 * @return {Array<AccessoryType>} - simple accessories with the same parent id
 */
export const getSimpleAccessoriesByParentId = createSelector(
    getCartAccessoriesSimple,
    (_: unknown, parentId: string | null): string | null => parentId,
    (accessories: Array<AccessoryType>, parentId: string | null): Array<AccessoryType> =>
        accessories
            .filter((accessory: AccessoryType) => accessory.parentId === parentId)
            .map((accessory: AccessoryType) => ({
                ...accessory,
                color: {
                    color_name: accessory.color_name,
                    id: accessory.id,
                    image: accessory.color_image,
                } as unknown as ColorType,
            })),
);

export const getCartAccessoriesInStock = createSelector(
    [getCartAccessories, getCartAccessoriesSimple],
    (wAccessories: Array<AccessoryWindowType>, accessories: Array<AccessoryType>) => {
        const windowIds = Array.from(
            new Set(accessories.map(({parentId}: AccessoryType): string => parentId)),
        );
        return wAccessories.filter(({id}: AccessoryWindowType): boolean => windowIds.includes(id));
    },
);

export const getDisplayedCartAccessoriesSimple = createSelector(
    [getCartAccessoriesInStock, getCartAccessoriesSimple],
    (wAccessoriesInStock: Array<AccessoryWindowType>, accessoriesSimple: Array<AccessoryType>) => {
        const windowIds = Array.from(
            new Set(wAccessoriesInStock.map(({id}: AccessoryWindowType): string => id)),
        );
        return accessoriesSimple.filter(({id}: AccessoryType): boolean => windowIds.includes(id));
    },
);

/**
 * Returns a list of not yet added accessories
 * @return {AccessoryWindowType[]} - list not added accessories
 */
export const getListOfNotAddedAccessories = createSelector(
    getCartAccessoriesInStock,
    (accessories: AccessoryWindowType[]) => accessories.filter(item => !item.is_added),
);

/**
 * Returns id of the first not added accessory
 * @return {string | null} - id or null
 */
export const getFirstNotAddedCartAccessoryId = createSelector(
    getListOfNotAddedAccessories,
    (accessories: AccessoryWindowType[]) => (accessories.length > 0 ? accessories[0].id : null),
);

/**
 * Returns the first child of the parent accessory without adding
 * @return {AccessoryType | undefined} - first child element
 */
export const getFirstSimpleAccessoriesByParentId = createSelector(
    (store: ApplicationStoreType) =>
        getSimpleAccessoriesByParentId(store, getFirstNotAddedCartAccessoryId(store)),
    simpleAccessories => simpleAccessories[0],
);

/**
 * Return accessories list on cart
 * @param {ApplicationStoreType} state - app store
 * @returns {number} accessory id which is changing in the cart at this moment
 */
export const getAccessoryLoadingId = (state: ApplicationStoreType): number =>
    state.cart.accessories.accessoryLoadingId as unknown as number;

/**
 * Is accessories disabled
 * @param {ApplicationStoreType} state - application store
 * @return {boolean} - is disabled
 */
export const isAccessoriesDisabled = (state: ApplicationStoreType): boolean =>
    state.cart.status === 'pending' || state.changeItemQty.status === 'pending';

/**
 * Get Added to cart Item by id
 * @param {ApplicationStoreType} store - Application store
 * @param {string} itemId - cart quote item id to get
 * @return {CartItemType|undefined} return active accessories
 */
export const getCartItemByProductId = createSelector(
    [getQuoteItems, (store, itemId) => itemId],
    (items, itemId: string) =>
        items.find(({product_id: productId}) => productId === itemId) as CartItemAccessoryType,
);

/**
 * Is eyeglasses related accessory which should be removed with item
 * @param {ApplicationStoreType} store - application store
 * @param {string} itemId - item id
 * @return {boolean} - is item related to accessory
 */
export const getIsRelatedToAccessory = (store: ApplicationStoreType, itemId: string): boolean =>
    store.cart.quoteItems.filter(
        item =>
            item.type === PRODUCT_TYPES.ACCESSORY &&
            Array.isArray(item.related_items) &&
            item.related_items.length === 1 &&
            item.related_items.includes(Number(itemId)),
    ).length > 0;

/**
 * Get is UHC applying allowance
 *
 * @param {ApplicationStoreType} store - redux stores
 * @returns {boolean} - is UHC applying allowance
 */
export const isUhcApplyingAllowance = (store: ApplicationStoreType): boolean =>
    store.cart.applyingAllowance;

/**
 * Get item from cart by id
 * @param {ApplicationStoreType} state - cart state
 * @param {string} itemId - item id
 * @returns {CartItemType} - item with specified id by props
 */
export const getCartItemById = (state: ApplicationStoreType, itemId: string): CartItemType =>
    state.cart.quoteItems.find((item: CartItemType) => item.id === itemId)!;

/**
 * Get added second frames from cart
 * @param {ApplicationStoreType} state - Application store
 * @return {Array<CartItemType>} - added second frames
 */
export const getSecondFrameCartItems = (state: ApplicationStoreType): Array<CartItemType> =>
    state.cart.quoteItems.filter(
        // @ts-expect-error
        item => item.badges && item.badges.some(({code}) => code === BADGES.UPSELLS),
    );

/**
 * Make memoized selector for checking on exist of second frame upsell on frames
 */
export const makeGetIsProductHasSecondFrame = () =>
    createSelector(
        [getSecondFrameCartItems, getCartItemById],
        (secondFrames: Array<CartItemType>, cartItem: CartItemType) =>
            secondFrames.some(
                frame => Number(frame.related_product) === Number(cartItem.product_id),
            ),
    );

/**
 * Is cart reducer and changeItemQty reducer not in pending state
 * @param {ApplicationStoreType} state - application store
 * @return {boolean} - is can change item qty
 */
export const isCanChangeItemQty = (state: ApplicationStoreType): boolean =>
    state.cart.status !== 'pending' && state.changeItemQty.status !== 'pending';

/**
 * Get is update item cart loading
 * @param {ApplicationStoreType} store - Application store
 * @returns {boolean} - is loaded
 */
export const getIsUpdateItemPending = (store: ApplicationStoreType): boolean =>
    store.cart.status === 'pending';

/**
 * Checking if cart has vista plus item
 */
export const getIsCartHasVista = createSelector(getQuoteItems, (items: Array<CartItemType>) =>
    items.some(item => item.brand === 'Vista Plus'),
);

/**
 * Is second frame upsell disabled
 * @param {ApplicationStoreType} state - application store
 * @returns {boolean} - is disabled
 */
export const isSecondFrameUpsellDisabled = (state: ApplicationStoreType): boolean =>
    state.addCartItemUpsell.status === 'pending' || state.cart.status === 'pending';

/**
 * Determines can user add an upsell or not
 *
 * @param {ApplicationStoreType} store - application store
 * @return {boolean} availability of upsell adding
 */
export const canAddUpsell = (store: ApplicationStoreType): boolean =>
    ![store.cartUpsells.status, store.cart.status].includes('pending');

export const isCurrentCartItemSunglasses = createSelector(
    getCartItemById,
    (item: CartItemType): boolean => item && item.type === PRODUCT_TYPES.SUNGLASSES,
);

/**
 * Function which generate selector by itemId from props gor getting upsells
 */
export const makeGetItemUpsells = () =>
    createSelector(
        getCartItemById,
        // @ts-expect-error
        (cartItem: CartItemType): UpsellOfferType[] => cartItem?.upsellData,
    );

/**
 * Get add cart item upsell
 * @param {ApplicationStoreType} state - store state
 * @return {*} - Cart upsells
 */
export const addCartItemUpsell = (state: ApplicationStoreType) => state.addCartItemUpsell;

/**
 * Gets payments info
 * @param {ApplicationStoreType} state - Application state
 * @returns {{amazon_v2: AmazonV2DataType, payPal: {}}} - payments info
 */
export const getPayments = (state: ApplicationStoreType) => state.payments;

/**
 * getAccessoriesActiveTab
 */
export const getAccessoriesActiveTab = (state: ApplicationStoreType) =>
    state.cart.accessories.selectedTab;

/**
 * getAccessoriesAvailableTabs
 */
export const getAccessoriesAvailableTabs = (state: ApplicationStoreType) =>
    state.cart.accessories.availableTabs;
